import { dashboardv20240509Client } from '@sequencehq/api/dashboard/v20240509'
import { IntegrationServices, useQuery } from '@sequencehq/api/utils'
import { Banner, Button, Flex } from '@sequencehq/core-components'
import { GreyGrey60 } from '@sequencehq/core-components'
import { MINUTES } from '@sequencehq/utils/dates'
import Spinner from 'components/Loading/Spinner'
import {
  ResolveCustomerLinkButton,
  useReconnectToIntegration
} from 'Integrations/index'
import { getIntegrationName } from 'Integrations/utils/getIntegrationName'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { willAutoLinkCustomers } from 'Integrations/integrationsConfig/common/AutoLinkCustomers/entityTypeSyncToggle.helpers'
import { useAtomValue } from 'jotai'
import {
  invoiceStatusAtom,
  invoiceCustomerIdAtom,
  invoiceHasNegativeTotalAtom
} from 'Invoices/atoms/invoiceEditing.atom'

const NegativeInvoiceWarning = () => {
  return (
    <Banner
      variant="warning"
      alignIcon="center"
      data-testid="invoice.warning.negative-invoice"
    >
      Negative invoices cannot be finalized
    </Banner>
  )
}

const useInvoiceIntegrationWarningBar = ({ enabled }: { enabled: boolean }) => {
  const customerId = useAtomValue(invoiceCustomerIdAtom)
  const invoiceStatus = useAtomValue(invoiceStatusAtom)

  const viableIntegrationsQuery = useQuery(
    dashboardv20240509Client.getIntegrations,
    undefined,
    {
      enabled,
      staleTime: 1 * MINUTES,
      select: response => {
        if (!response) {
          return []
        }

        return (
          response.items
            .filter(
              int =>
                int.syncedEntityTypes.includes('INVOICE') &&
                int.state === 'CONNECTED'
            )
            .map(int => ({
              syncedEntityTypes: int.syncedEntityTypes,
              service: int.service,
              authFailure: int.authFailure
            })) ?? []
        )
      }
    }
  )

  const customerQuery = useQuery(
    dashboardv99990101Client.getCustomer,
    {
      id: customerId ?? ''
    },
    {
      enabled: !!customerId && enabled,
      select: (
        customer
      ): {
        name: string
        externalLinks: Partial<{ [service in IntegrationServices]: string }>
      } | null => {
        if (!customer) {
          return null
        }
        return {
          name: customer.legalName,
          externalLinks: customer.integrationIds.reduce(
            (acc, customerLink) => ({
              ...acc,
              [customerLink.service]: customerLink.id
            }),
            {}
          )
        }
      }
    }
  )

  /**
   * Services with customer auto-linking switched on. When on, an unlinked
   * customer will be pushed automatically on finalise/send, so the warning
   * softens from "won't be synced" to "we'll try to link them".
   */
  const autoLinkEnabledQuery = useQuery(
    dashboardv99990101Client.getEntityTypeSyncToggles,
    undefined,
    {
      enabled,
      staleTime: 1 * MINUTES,
      select: response =>
        new Set(
          (response?.items ?? [])
            .filter(willAutoLinkCustomers)
            .map(toggle => toggle.integrationService)
        )
    }
  )

  const authFailures = viableIntegrationsQuery.data
    ?.filter(int => int.authFailure)
    .map(int => ({
      service: int.service,
      serviceName: getIntegrationName(int.service)
    }))

  const missingCustomerLinks = viableIntegrationsQuery.data
    ?.filter(
      int =>
        int.syncedEntityTypes.includes('CUSTOMER') &&
        !customerQuery.data?.externalLinks[int.service]
    )
    /**
     * Don't bother with a warning if we're already showing one for an auth failure
     */
    .filter(int => !authFailures?.find(af => af.service === int.service))
    .map(int => ({
      service: int.service,
      serviceName: getIntegrationName(int.service),
      autoLinkEnabled: autoLinkEnabledQuery.data?.has(int.service) ?? false
    }))

  const isInvoiceFinalized = ['FINAL', 'SENT'].includes(invoiceStatus ?? '')

  const { reconnect, reconnecting } = useReconnectToIntegration()

  return {
    /**
     * Auto-link state is supplementary — it only softens the warning wording,
     * so it deliberately does NOT gate isLoading. Blocking on it would hide the
     * existing customer-link/auth warnings whenever that query is slow.
     */
    isLoading: viableIntegrationsQuery.isPending || customerQuery.isPending,
    customerName: customerQuery.data?.name,
    customerId: customerId ?? '',
    missingCustomerLinks,
    authFailures,
    reconnect,
    reconnecting,
    isInvoiceFinalized
  }
}

const buildWillSyncMessage = ({
  customerName,
  serviceName
}: {
  customerName: string | undefined
  serviceName: string
}): string =>
  `${customerName ?? 'The customer'} isn't linked to ${serviceName}. We'll try to link them automatically when this invoice is finalized or sent.`

const buildWontSyncMessage = ({
  customerName,
  serviceName,
  isInvoiceFinalized
}: {
  customerName: string | undefined
  serviceName: string
  isInvoiceFinalized: boolean
}): string => {
  const syncability = isInvoiceFinalized ? "can't" : "won't"
  return `Invoice ${syncability} be synced to ${serviceName} because ${customerName ?? 'the customer'} isn't linked.`
}

// IntegrationWarnings Component - handles integration-specific warnings
const IntegrationWarnings = ({
  hook
}: {
  hook: ReturnType<typeof useInvoiceIntegrationWarningBar>
}) => {
  if (
    hook.isLoading ||
    (hook.missingCustomerLinks?.length === 0 && hook.authFailures?.length === 0)
  ) {
    return null
  }

  return (
    <>
      {hook.missingCustomerLinks?.map(int => {
        const warningMessage = int.autoLinkEnabled
          ? buildWillSyncMessage({
              customerName: hook.customerName,
              serviceName: int.serviceName
            })
          : buildWontSyncMessage({
              customerName: hook.customerName,
              serviceName: int.serviceName,
              isInvoiceFinalized: hook.isInvoiceFinalized
            })

        return (
          <Banner
            variant="warning"
            alignIcon="center"
            key={`${int.service}-customer`}
            data-testid={`invoice.integrationWarnings.${int.service}.customer`}
            action={
              <ResolveCustomerLinkButton
                service={int.service}
                customerId={hook.customerId}
              />
            }
          >
            {warningMessage}
          </Banner>
        )
      })}
      {hook.authFailures?.map(int => (
        <Banner
          variant="warning"
          alignIcon="center"
          key={`${int.service}-auth`}
          data-testid={`invoice.integrationWarnings.${int.service}.auth`}
          action={
            <Button
              variant="secondary"
              onClick={() => {
                void hook.reconnect(int.service)
              }}
              disabled={hook.reconnecting}
            >
              <Flex gap="8px" alignItems="center">
                {hook.reconnecting && (
                  <Spinner height="16px" width="16px" color={GreyGrey60} />
                )}{' '}
                {hook.reconnecting ? 'Resolving...' : 'Resolve'}
              </Flex>
            </Button>
          }
        >
          Invoice {hook.isInvoiceFinalized ? "can't" : "won't"} be synced to{' '}
          {int.serviceName} because of an authentication failure.
        </Banner>
      ))}
    </>
  )
}

export const InvoiceWarningBanners = () => {
  const invoiceStatus = useAtomValue(invoiceStatusAtom)
  const hasNegativeTotal = useAtomValue(invoiceHasNegativeTotalAtom)

  // Only show warnings for non-finalized invoices
  const isInvoiceFinalized = ['FINAL', 'SENT'].includes(invoiceStatus ?? '')

  const integrationWarningsHook = useInvoiceIntegrationWarningBar({
    enabled: !isInvoiceFinalized
  })

  const showIntegrationWarnings =
    !integrationWarningsHook.isLoading &&
    (integrationWarningsHook.missingCustomerLinks?.length !== 0 ||
      integrationWarningsHook.authFailures?.length !== 0)

  const showNegativeInvoiceWarning =
    invoiceStatus === 'DRAFT' && hasNegativeTotal

  if (
    isInvoiceFinalized ||
    (!showNegativeInvoiceWarning && !showIntegrationWarnings)
  ) {
    return null
  }

  return (
    <Flex flexDirection="column" gap="8px">
      {showNegativeInvoiceWarning && <NegativeInvoiceWarning />}
      <IntegrationWarnings hook={integrationWarningsHook} />
    </Flex>
  )
}
